import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import { WhatsGlyph } from "@/pages/home/icons";
import { ATENDENTE_FONE, atendenteUrl } from "@/pages/home/data";

/**
 * "Falar com atendente" (WhatsApp) — BARRA FIXA ancorada na borda inferior.
 *
 * HISTÓRICO:
 *  - até 14/10/2026: bolinha de 40px `md:hidden` (sumia no desktop, nada dizia
 *    que ali se fala com uma pessoa);
 *  - 14/10/2026: virou pílula flutuante no canto inferior direito, com rótulo;
 *  - 14/10/2026 (2ª): virou BARRA de largura total ancorada na borda inferior;
 *  - 15/10/2026, pedido do dono: a barra fixa cobria conteúdo e incomodava
 *    ("tira esse negócio fixo de baixo"). O bloco completo "Dúvidas? Fale com um
 *    atendente" passou a viver no RODAPÉ da página (ver `Rodape` em
 *    home/sections.tsx) e aqui ficou APENAS um botão redondo fixo à direita.
 *
 * Por que só o ícone: um botão pequeno no canto não tapa texto nem exige que a
 * página reserve espaço embaixo (o `pb-[calc(76px+…)]` do `lt-root` foi
 * removido junto). O rótulo acessível vive no `aria-label` + `title`, então
 * leitor de tela e tooltip continuam dizendo do que se trata.
 *
 * Fundo sólido de propósito (sem backdrop-blur): elemento fixo com blur é
 * recalculado a cada quadro de rolagem no celular.
 * Ícone em SVG inline: aparece no 1º quadro, antes da fonte Remix.
 * Mantidos o evento `whatsapp_floating_click` (Marketing) e a mensagem pronta.
 */
export default function FloatingWhatsApp({ anyPage = false }: { anyPage?: boolean } = {}) {
  const location = useLocation();
  // Apenas na landing page (ou onde for pedido explicitamente, ex.: cadastro de paciente)
  const isLanding = anyPage || location.pathname === "/";
  const phone = ATENDENTE_FONE;
  const whatsappUrl = atendenteUrl();

  const handleClick = () => {
    trackEvent("whatsapp_floating_click", "floating_bubble", { phone });
  };

  if (!isLanding) return null;

  // Portal no <body>: dentro de um pai com `transform` (ex.: animação de entrada do
  // cadastro) o `position: fixed` passa a ser relativo ao pai e a bolinha rolava junto.
  return createPortal(
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      aria-label="Falar com um atendente pelo WhatsApp"
      title="Falar com um atendente"
      className="lt-bar fixed z-40 right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] sm:right-5 sm:bottom-5 w-14 h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white grid place-items-center shadow-[0_14px_32px_-10px_rgba(5,150,105,.6)] ring-1 ring-black/5 transition-colors"
    >
      <WhatsGlyph className="w-7 h-7" />
      <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-emerald-300 ring-2 ring-emerald-600" aria-hidden="true" />
    </a>,
    document.body
  );
}
